import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1682888818620-94875adf5bb9?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");

  useEffect(() => {
    api.get("/posts", { params: category ? { category } : {} })
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [category]);

  const categories = ["", "Home Care", "Office Health", "Seasonal"];

  return (
    <div>
      <Seo
        title="Cleaning Tips & Guides Blog | Glow Green - St. Louis & Chesterfield"
        description="Practical cleaning guides from Glow Green: house cleaning checklists, deep cleaning advice, office cleanliness, and seasonal tips for St. Louis and Chesterfield homes and businesses."
      />
      <section className="bg-forest grain relative pt-32 lg:pt-40 pb-16 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10">
          <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf">The Glow Green Journal</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            Cleaner spaces,<br />explained simply.
          </h1>
          <p className="mt-5 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed">
            Practical guides for homes and businesses across the St. Louis area - no fluff, just what works.
          </p>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex flex-wrap gap-2 mb-10" role="group" aria-label="Filter by category">
            {categories.map((c) => (
              <button
                key={c || "all"}
                data-testid={`blog-filter-${c ? c.toLowerCase().replace(/\s+/g, "-") : "all"}`}
                onClick={() => setCategory(c)}
                aria-pressed={category === c}
                className={`rounded-full px-4 py-2 text-xs font-semibold tracking-wide transition-all ${
                  category === c ? "bg-forest text-white" : "bg-white border border-forest/15 text-forest hover:border-leaf"
                }`}
              >
                {c || "All Articles"}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-slate-500" data-testid="blog-loading">Loading articles...</p>
          ) : posts.length === 0 ? (
            <p className="text-slate-500" data-testid="blog-empty">No articles in this category yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="blog-grid">
              {posts.map((post, i) => (
                <Reveal key={post.id} delay={i * 0.06}>
                  <Link
                    to={`/blog/${post.slug}`}
                    data-testid={`blog-card-${post.slug}`}
                    className="group block rounded-2xl overflow-hidden bg-white border border-forest/10 hover:border-leaf/40 hover:shadow-xl hover:shadow-leaf/5 transition-all h-full"
                  >
                    <div className="overflow-hidden aspect-[16/10]">
                      <img
                        src={post.image || FALLBACK_IMG}
                        alt={post.image_alt || post.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-xs">
                        <span className="rounded-full bg-mint px-3 py-1 font-semibold text-forest">{post.category}</span>
                        <time dateTime={post.published_at} className="text-slate-400">
                          {format(new Date(post.published_at), "MMM d, yyyy")}
                        </time>
                      </div>
                      <h2 className="mt-3 font-display text-lg font-bold text-forest leading-snug group-hover:text-leaf-hover transition-colors">
                        {post.title}
                      </h2>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">{post.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
